interface ROISectionProps {
  onCTAClick: () => void;
}

const flowSteps = [
  {
    icon: "ri-group-line",
    label: "Targeted Agents",
    value: "2,500",
    sub: "High-producing agents contacted monthly",
    color: "#19a2fd",
  },
  {
    icon: "ri-phone-line",
    label: "Conversations",
    value: "500",
    sub: "20% pickup rate",
    color: "#19a2fd",
  },
  {
    icon: "ri-lightbulb-flash-line",
    label: "Opportunities",
    value: "100",
    sub: "20% conversation-to-opportunity rate",
    color: "#f1663a",
  },
  {
    icon: "ri-key-2-line",
    label: "Closings Won",
    value: "15–20",
    sub: "15–20% opportunity conversion",
    color: "#f1663a",
  },
  {
    icon: "ri-money-dollar-circle-line",
    label: "Revenue Generated",
    value: "$30K–$40K",
    sub: "Monthly @ $2,000 per closing",
    color: "#f1663a",
  },
];

export default function ROISection({ onCTAClick }: ROISectionProps) {
  return (
    <section className="bg-white py-14 md:py-24 px-6 lg:px-8">
      <div className="max-w-7xl mx-auto">

        {/* Section header */}
        <div className="text-center max-w-2xl mx-auto mb-10 md:mb-16">
          <div className="inline-flex items-center gap-2 bg-[#f4f7fb] border border-gray-200 rounded-full px-4 py-1.5 mb-6">
            <i className="ri-bar-chart-box-line text-[#0b1f33] text-xs" />
            <span className="text-xs font-semibold text-[#0b1f33]">The Math Is Simple</span>
          </div>
          <h2
            className="text-[#0b1f33] font-bold mb-4"
            style={{ fontSize: "clamp(26px, 3vw, 38px)", lineHeight: "1.2" }}
          >
            What This Means in Your Market
          </h2>
          <p className="text-[#4a5568] text-base" style={{ lineHeight: "1.65" }}>
            Why timing and scale determine who wins the closing — and who never even gets the opportunity.
          </p>
        </div>

        {/* Market Context Stat Cards */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-10 md:mb-16">
          {[
            {
              icon: "ri-map-2-line",
              stat: "238,000",
              label: "Active Agents in Florida",
              color: "#19a2fd",
            },
            {
              icon: "ri-vip-crown-2-line",
              stat: "47,600",
              label: "Top 20% Drive the Majority of Closings",
              color: "#f1663a",
            },
            {
              icon: "ri-repeat-2-line",
              stat: "20+",
              label: "Closings Per Year from Top Agents",
              color: "#0b1f33",
            },
          ].map((card) => (
            <div
              key={card.stat}
              className="bg-[#f4f7fb] rounded-2xl px-5 py-5 flex items-center gap-4"
            >
              <div
                className="w-11 h-11 rounded-xl flex items-center justify-center flex-shrink-0"
                style={{ backgroundColor: card.color + "18" }}
              >
                <i className={`${card.icon} text-lg`} style={{ color: card.color }} />
              </div>
              <div>
                <p className="text-[#0b1f33] font-bold text-2xl leading-none mb-1">{card.stat}</p>
                <p className="text-[#4a5568] text-sm leading-snug">{card.label}</p>
              </div>
            </div>
          ))}
        </div>

        {/* Manual Outreach Limitation block */}
        <div className="rounded-2xl border border-red-100 bg-red-50/50 p-5 md:p-8 mb-10 md:mb-16">
          <div className="flex items-center gap-3 mb-5">
            <div className="w-9 h-9 rounded-full bg-red-100 flex items-center justify-center flex-shrink-0">
              <i className="ri-error-warning-line text-red-500 text-base" />
            </div>
            <h3 className="text-[#0b1f33] font-bold text-lg">The Reality of Manual Outreach</h3>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-5 mb-6">
            {/* 476 days */}
            <div className="flex items-start gap-4">
              <div className="w-9 h-9 rounded-lg bg-white border border-red-200 flex items-center justify-center flex-shrink-0 mt-0.5">
                <i className="ri-calendar-2-line text-red-400 text-base" />
              </div>
              <div>
                <p className="text-[#0b1f33] font-bold text-xl leading-none mb-1">476 days</p>
                <p className="text-[#4a5568] text-sm leading-snug">to reach 47,600 agents at 100 dials per day</p>
              </div>
            </div>

            {/* $146,400+ — expanded math card */}
            <div className="flex items-start gap-4">
              <div className="w-9 h-9 rounded-lg bg-white border border-red-200 flex items-center justify-center flex-shrink-0 mt-0.5">
                <i className="ri-money-dollar-circle-line text-red-400 text-base" />
              </div>
              <div className="flex-1 min-w-0">
                <p className="text-[#0b1f33] font-bold text-xl leading-none mb-1">$146,400+</p>
                <p className="text-[#4a5568] text-sm leading-snug mb-3">Cost to cover that effort with a single rep</p>

                {/* Math breakdown */}
                <div className="rounded-xl border border-red-100 bg-white overflow-hidden text-xs">
                  <div className="px-3 py-1.5 bg-red-50 border-b border-red-100 flex items-center gap-1.5">
                    <i className="ri-calculator-line text-red-400 text-xs" />
                    <span className="text-red-500 font-semibold uppercase tracking-wide text-[10px]">The Math</span>
                  </div>
                  <div className="px-3 py-2.5 space-y-1.5">
                    <div className="flex justify-between gap-2 text-[#4a5568]">
                      <span>Base Salary</span>
                      <span className="font-semibold text-[#0b1f33] tabular-nums whitespace-nowrap">$60,000</span>
                    </div>
                    <div className="flex justify-between gap-2 text-[#4a5568]">
                      <span>Benefits &amp; Burden</span>
                      <span className="font-semibold text-[#0b1f33] tabular-nums whitespace-nowrap">+ $20,000</span>
                    </div>
                    <div className="flex justify-between gap-2 text-[#4a5568] border-t border-dashed border-red-100 pt-1.5">
                      <span className="font-medium">True Annual Cost</span>
                      <span className="font-bold text-[#0b1f33] tabular-nums whitespace-nowrap">$80,000 / yr</span>
                    </div>
                    <div className="flex justify-between gap-2 text-[#4a5568]">
                      <span>Time to Reach Market</span>
                      <span className="font-semibold text-[#0b1f33] tabular-nums whitespace-nowrap">× 1.83 yrs</span>
                    </div>
                    <div className="flex justify-between gap-2 border-t border-dashed border-red-100 pt-1.5">
                      <span className="font-bold text-[#0b1f33]">Total Cost</span>
                      <span className="font-bold text-red-500 tabular-nums whitespace-nowrap">= $146,400</span>
                    </div>
                  </div>
                  <div className="px-3 py-1.5 bg-red-50 border-t border-red-100">
                    <p className="text-[10px] text-red-400 italic leading-snug">
                      ⚠ Assumes 100 dials/day at 100% connection rate — an impossible standard.
                    </p>
                  </div>
                </div>
              </div>
            </div>

            {/* $15 per Conversation */}
            <div className="flex items-start gap-4">
              <div className="w-9 h-9 rounded-lg bg-white border border-red-200 flex items-center justify-center flex-shrink-0 mt-0.5">
                <i className="ri-chat-1-line text-red-400 text-base" />
              </div>
              <div>
                <p className="text-[#0b1f33] font-bold text-xl leading-none mb-1">$15 per Conversation</p>
                <p className="text-[#4a5568] text-sm leading-snug">based on typical pickup rates</p>
              </div>
            </div>
          </div>

          <div className="flex items-start gap-3 bg-white rounded-xl border border-red-200 px-4 py-3.5">
            <i className="ri-flashlight-line text-[#f1663a] text-base mt-0.5 flex-shrink-0" />
            <p className="text-[#0b1f33] font-semibold text-sm leading-relaxed">
              $146,400 to touch the market one time. And you still haven&apos;t reached them at the moment that matters.
            </p>
          </div>
        </div>

        {/* AI-Powered ROI Flow */}
        <div className="mb-10 md:mb-16">
          <div className="flex items-center gap-3 mb-2">
            <div className="w-9 h-9 rounded-full bg-[#19a2fd]/15 flex items-center justify-center flex-shrink-0">
              <i className="ri-robot-2-line text-[#19a2fd] text-base" />
            </div>
            <h3 className="text-[#0b1f33] font-bold text-lg">What Winning Looks Like in Your Market</h3>
          </div>

          {/* Funnel label */}
          <div className="mb-6 pl-12">
            <span className="inline-flex items-center gap-1.5 text-xs font-semibold text-[#19a2fd] uppercase tracking-widest">
              <i className="ri-arrow-right-line text-xs" />
              From First Conversation to Closing
            </span>
          </div>

          <div className="relative">
            {/* Connector line (desktop) */}
            <div className="hidden lg:block absolute top-[42px] left-[10%] right-[10%] h-px bg-gradient-to-r from-[#19a2fd]/30 via-[#f1663a]/40 to-[#f1663a]/30 z-0" />

            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-5 gap-5 relative z-10">
              {flowSteps.map((step, i) => (
                <div
                  key={step.label}
                  className="bg-[#f4f7fb] rounded-2xl px-5 py-5 flex flex-col items-center text-center relative"
                >
                  {/* Step number */}
                  <div className="absolute -top-3 left-4 w-6 h-6 rounded-full bg-white border border-gray-200 flex items-center justify-center">
                    <span className="text-xs font-bold text-[#4a5568]">{i + 1}</span>
                  </div>

                  <div
                    className="w-11 h-11 rounded-xl flex items-center justify-center mb-3 flex-shrink-0"
                    style={{ backgroundColor: step.color + "18" }}
                  >
                    <i className={`${step.icon} text-lg`} style={{ color: step.color }} />
                  </div>
                  <p className="text-xs font-semibold text-[#4a5568] uppercase tracking-wide mb-1">
                    {step.label}
                  </p>
                  <p
                    className="font-bold text-2xl leading-none mb-2"
                    style={{ color: step.color }}
                  >
                    {step.value}
                  </p>
                  <p className="text-xs text-[#4a5568] leading-snug">{step.sub}</p>
                </div>
              ))}
            </div>
          </div>

          {/* Framing language */}
          <p className="text-center text-xs text-[#4a5568] mt-5 italic">
            Based on typical performance in high-activity markets
          </p>
        </div>

        {/* Break-even + Transparency row */}
        <div className="grid grid-cols-1 lg:grid-cols-5 gap-5 mb-10">
          {/* Break-even highlight */}
          <div className="lg:col-span-2 rounded-2xl bg-[#0b1f33] text-white px-5 md:px-8 py-6 md:py-7 flex flex-col justify-between">
            <div>
              <div className="inline-flex items-center gap-2 bg-[#f1663a]/20 rounded-full px-3 py-1 mb-5">
                <i className="ri-fire-line text-[#f1663a] text-xs" />
                <span className="text-xs font-semibold text-[#f1663a]">Break-Even Point</span>
              </div>
              <h4 className="text-white font-bold text-xl leading-snug mb-3">
                You only need{" "}
                <span className="text-[#f1663a]">8-10 additional closings</span>{" "}
                per month to cover the cost of the platform.
              </h4>
              <p className="text-white/60 text-sm leading-relaxed">
                Everything beyond that becomes incremental revenue — and with AI running 24/7, the pipeline never stops.
              </p>
            </div>
            <div className="mt-6">
              <p className="text-white/70 text-xs leading-relaxed mb-4 italic">
                These conversations are already happening in your market — the question is whether you're part of them.
              </p>
              <button
                onClick={onCTAClick}
                className="w-full bg-[#f1663a] hover:bg-orange-600 text-white font-semibold text-sm py-3 rounded-xl transition-colors cursor-pointer whitespace-nowrap"
              >
                Secure Your Market Before It's Gone
              </button>
            </div>
          </div>

          {/* Transparency + disclaimer */}
          <div className="lg:col-span-3 rounded-2xl bg-[#f4f7fb] border border-gray-200 px-5 md:px-8 py-6 md:py-7 flex flex-col justify-between">
            <div>
              <div className="flex items-center gap-2 mb-4">
                <i className="ri-information-line text-[#19a2fd] text-base" />
                <span className="text-sm font-semibold text-[#0b1f33]">How This Model Works</span>
              </div>
              <p className="text-[#4a5568] text-sm leading-relaxed mb-5">
                This model focuses on the{" "}
                <strong className="text-[#0b1f33]">top-performing 20% of agents — the ones actually driving closings in your market.</strong>{" "}
                AI-driven outreach enables{" "}
                <strong className="text-[#0b1f33]">immediate engagement based on real-time listing and transaction activity</strong>,
                improving timing, scale, and consistency compared to manual outreach.
              </p>

              {/* Mini comparison table */}
              <div className="rounded-xl overflow-hidden border border-gray-200">
                <div className="grid grid-cols-3 bg-[#0b1f33] text-white text-xs font-semibold">
                  <div className="px-2.5 md:px-4 py-2.5">Metric</div>
                  <div className="px-2.5 md:px-4 py-2.5 text-center border-l border-white/10">Manual</div>
                  <div className="px-2.5 md:px-4 py-2.5 text-center border-l border-white/10 text-[#19a2fd]">AI-Powered</div>
                </div>
                {[
                  ["Agents / Mo", "~2,000", "2,500+ targeted"],
                  ["Time to Market", "476 days", "30 days"],
                  ["Timing", "None", "Real-time"],
                  ["Cost / Outreach", "$146K+ / run", "$15 / convo"],
                ].map(([metric, manual, ai]) => (
                  <div
                    key={metric}
                    className="grid grid-cols-3 text-xs border-t border-gray-100 bg-white"
                  >
                    <div className="px-2.5 md:px-4 py-2.5 text-[#4a5568] font-medium">{metric}</div>
                    <div className="px-2.5 md:px-4 py-2.5 text-center text-red-400 border-l border-gray-100">{manual}</div>
                    <div className="px-2.5 md:px-4 py-2.5 text-center text-[#19a2fd] font-semibold border-l border-gray-100">{ai}</div>
                  </div>
                ))}
              </div>
            </div>

            <p className="text-[10px] text-gray-400 leading-relaxed mt-5">
              Illustrative example only. Results are not guaranteed and will vary based on outreach volume, agent responsiveness, and market conditions.
            </p>
          </div>
        </div>

      </div>
    </section>
  );
}
